import { formatDate } from "../lib/format";
import type { Limitation } from "../lib/legal";
import { LegalDisclaimer } from "./LegalDisclaimer";

/**
 * Исковая давность (E9, ТЗ 4.2.5): дата истечения + предупреждение + примечание о приостановлении.
 *
 * Нормы: 3 года (ст. 196 ГК РФ) с окончания срока исполнения (п. 2 ст. 200 ГК РФ); предупреждение
 * юристу за 6 месяцев (Прил. В). Дата НЕ сдвигается автоматически при наличии претензии —
 * п. 3 ст. 202 ГК РФ применяет юрист, о чём и говорит примечание.
 */
export function LimitationCard({ limitation }: { limitation: Limitation }) {
  if (limitation.limitDate === null) {
    return (
      <div className="rounded-[var(--radius-l)] border border-border bg-surface p-3">
        <h3 className="text-[15px] font-bold text-ink">Исковая давность</h3>
        <p className="mt-1 text-[13px] text-muted">
          Срок исполнения не определён, поэтому исковая давность не исчисляется.
        </p>
        <LegalDisclaimer />
      </div>
    );
  }

  const tone = limitation.expired
    ? "bg-overdue-bg text-overdue-fg"
    : limitation.warn
      ? "bg-waiting-bg text-waiting-fg"
      : "bg-bg text-muted";

  return (
    <div className="rounded-[var(--radius-l)] border border-border bg-surface p-3">
      <h3 className="text-[15px] font-bold text-ink">Исковая давность</h3>

      <dl className="mt-2 grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted">Истекает</dt>
          <dd className="text-ink">{formatDate(limitation.limitDate)}</dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-muted">Осталось</dt>
          <dd className="num text-ink">
            {limitation.daysLeft === null
              ? "—"
              : limitation.expired
                ? "срок истёк"
                : `${limitation.daysLeft} дн.`}
          </dd>
        </div>
      </dl>

      <p
        className={`mt-2 rounded-[var(--radius-m)] px-2.5 py-1.5 text-[13px] ${tone}`}
        role="status"
      >
        {limitation.expired
          ? "Срок исковой давности истёк (ст. 196 ГК РФ). Обращение в суд возможно, но ответчик вправе заявить о пропуске срока."
          : limitation.warn
            ? "До истечения срока исковой давности осталось менее шести месяцев — требуется решение о предъявлении иска."
            : "Три года с окончания срока исполнения (ст. 196, п. 2 ст. 200 ГК РФ)."}
      </p>

      {limitation.suspensionNote !== null && (
        <p className="mt-2 rounded-[var(--radius-m)] bg-bg px-2.5 py-1.5 text-[13px] text-muted">
          {limitation.suspensionNote}
        </p>
      )}

      <LegalDisclaimer />
    </div>
  );
}
